<!-- AddPermissionForm.vue -->
<script setup>
import { ref, toRef, computed, nextTick, watch, onBeforeUnmount, defineExpose } from "vue";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import { useUserStore } from "@/stores/useUserStore";
import { useManagedAccountStore } from "@/stores/useManagedAccountStore";
import { getContextManagerUserId } from "@/utils/permissionDefaults";
import api from "@/services/api";

// PROPS E EMITS
const props = defineProps({
  payload: { type: Array, default: () => [] },
  entityType: { type: String, default: "Text" },
  users: { type: Array, default: () => [] },
  groups: { type: Array, default: () => [] },
  permissions: { type: Array, default: () => [] },
  creatorUserName: { type: String, default: null },
});
const emit = defineEmits(["update:payload"]);

// STORE DO USUÁRIO LOGADO
const userStore = useUserStore();
const loggedUserId = computed(() => userStore.currentUser?.userId ?? null);

// 🔹 Círculo de contas gerenciadas: titulares de quem o logado é membro e
// membros que ele gerencia. Os grupos criados por eles também ficam
// disponíveis no seletor (ver `filteredGroups`).
const managedAccountStore = useManagedAccountStore();
managedAccountStore.ensureRelatedUsers();
const relatedUserIds = computed(() => managedAccountStore.relatedUserIds);

// 🔹 Titular do contexto de login ativo — null quando o logado não é membro
// gerenciado. Usado para travar a linha dele na tabela de permissões.
const managerUserId = computed(() => getContextManagerUserId());

// 🔹 ID do usuário criador (derivado do nome + lista de users recebidos na prop)
const creatorUserId = computed(() => {
  if (!props.creatorUserName || !props.users?.length) return null;
  const user = props.users.find(
    (u) => (u.name || "").toLowerCase() === props.creatorUserName.toLowerCase()
  );
  return user ? user.id : null;
});

// ESTADO REATIVO
const mappedPermissions = ref([]);
const payloadRef = toRef(props, "payload");
const selectedOption = ref("groups");
const selectedUser = ref(null);
const selectedGroup = ref(null);
const selectedPermissions = ref([]);
const isEditing = ref(false);
const editIndex = ref(null);
const nameInput = ref(null);
const search = ref("");

// ====================================================================
// ERROS INLINE (um por campo)
// ====================================================================
const inlineErrorEntity = ref("");
const inlineErrorPermissions = ref("");
const inlineErrorAction = ref("");

function clearInlineError() {
  inlineErrorEntity.value = "";
  inlineErrorPermissions.value = "";
  inlineErrorAction.value = "";
}

// ====================================================================
// AVATAR (espelho de PermissionView.vue)
// ====================================================================
function normalizeAvatarPath(path) {
  if (!path) return "";

  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;

  return `${base}${cleanPath}`;
}

function getAvatarUrl(photoPath) {
  if (!photoPath) return "";
  return normalizeAvatarPath(photoPath);
}

// 🔹 Resolve o photoPath de um usuário a partir do objeto (normalizado pelo pai)
function resolveUserPhotoPath(user) {
  if (!user) return null;
  return (
    user.photoPath ||
    user.photoUrl ||
    user.userPhotoPath ||
    user.photo_path ||
    user.user_photo_path ||
    null
  );
}

// 🔹 Busca o photoPath de um usuário na prop `users` pelo id
function getUserPhotoPathById(userId) {
  if (userId == null) return null;
  const user = (props.users || []).find((u) => u.id === userId);
  return resolveUserPhotoPath(user);
}

// ✅ normaliza creatorId (aceita PascalCase e camelCase)
function getGroupCreatorId(group) {
  return group?.createdByUserId ?? group?.CreatedByUserId ?? null;
}

// ✅ log completo dos grupos (sem "comer" nada)
function logAllGroups(label) {
  const list = (props.groups || []).map((g) => ({
    id: g.id,
    name: g.name,
    createdByUserId: getGroupCreatorId(g),
  }));

  console.log(`📦 ${label} | Total: ${list.length}`, list);

  if (props.groups?.length) {
    console.log("🔎 Primeiro grupo (raw):", props.groups[0]);
    console.log("🔎 Keys do primeiro grupo:", Object.keys(props.groups[0] || {}));
  } else {
    console.warn("⚠️ props.groups veio vazio.");
  }
}

// 🔹 helper: saber se a linha é do "proprietário" (criador)
function isOwnerEntry(entry) {
  if (entry.type !== "usuário") return false;
  const id = entry.entityId;
  return creatorUserId.value != null && id === creatorUserId.value;
}

// 🔹 helper: a linha é do titular do vínculo de conta gerenciada.
// Só existe para quem está logado como membro — o titular, no próprio
// formulário, não vê linha travada nenhuma por este motivo.
function isManagerEntry(entry) {
  if (entry.type !== "usuário") return false;
  if (managerUserId.value == null) return false;
  if (isOwnerEntry(entry)) return false; // criador manda no selo, sem duplicar
  return Number(entry.entityId) === Number(managerUserId.value);
}

// 🔹 Precedência usuário > grupo (espelha Authorization/PermissionRules no
// backend): a permissão dada direto a um usuário SUBSTITUI integralmente a que
// ele receberia pelos grupos dele — para restringir ou para ampliar. Aqui só
// avisamos quem lê a tabela; quem decide é o backend.
function groupMemberIds(group) {
  const list = group?.members ?? group?.users ?? group?.Users ?? [];
  return list
    .map((u) => Number(u.userId ?? u.UserId ?? u.id))
    .filter((id) => Number.isFinite(id));
}

// Nomes dos grupos presentes NESTA tabela aos quais o usuário da linha pertence
// — são os que a linha dele substitui. Vazio quando não há sobreposição ou
// quando o backend não mandou os membros do grupo.
function overriddenGroupNames(entry) {
  if (entry.type !== "usuário") return [];

  const userId = Number(entry.entityId);

  return mappedPermissions.value
    .filter((other) => other.type === "grupo")
    .filter((other) => {
      const group = (props.groups || []).find((g) => g.id === other.entityId);
      return group ? groupMemberIds(group).includes(userId) : false;
    })
    .map((other) => other.name);
}

// 🔹 linhas que o usuário não pode editar nem apagar. O titular entra aqui
// porque o acesso dele ao que o membro cria não é negociável: os moldes o
// semeiam (utils/permissionDefaults.js) e a tabela não desfaz.
function isLockedEntry(entry) {
  return isOwnerEntry(entry) || isManagerEntry(entry);
}

function updateMappedPermissionsFromPayload(payload) {
  if (
    !payload ||
    props.users.length === 0 ||
    props.groups.length === 0 ||
    props.permissions.length === 0
  ) {
    mappedPermissions.value = [];
    return;
  }

  const grouped = {};
  payload.forEach((p) => {
    const isUser = p.type === "user";
    const key = isUser ? `user-${p.entityId}` : `group-${p.entityId}`;

    const entityList = isUser ? props.users : props.groups;
    const entity = entityList.find((e) => e.id === p.entityId);
    const permission = props.permissions.find((perm) => perm.id === p.permissionId);

    if (!entity || !permission) return;

    if (!grouped[key]) {
      grouped[key] = {
        entityId: entity.id,
        name: entity.name,
        type: isUser ? "usuário" : "grupo",
        permissions: [],
        permissionIds: [],
        photoPath: isUser ? resolveUserPhotoPath(entity) : null,
      };
    }

    grouped[key].permissions.push(permission.name);
    grouped[key].permissionIds.push(permission.id);
  });

  mappedPermissions.value = Object.values(grouped);
}

watch(
  [payloadRef, () => props.users, () => props.groups, () => props.permissions],
  ([newPayload]) => {
    updateMappedPermissionsFromPayload(newPayload);
  },
  { deep: true, immediate: true }
);

// ✅ LOG: sempre que a lista de grupos mudar, loga TODOS os grupos do backend
watch(
  () => props.groups,
  () => {
    logAllGroups("Grupos RECEBIDOS do backend (props.groups)");
  },
  { deep: true, immediate: true }
);

// entries (com busca opcional)
const entriesToDisplay = computed(() => {
  if (search.value) {
    return mappedPermissions.value.filter((p) => {
      const name = p.name || "";
      const searchTerm = search.value || "";
      return name.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }
  return mappedPermissions.value;
});

// Lógica de filtragem dos grupos
const filteredGroups = computed(() => {
  logAllGroups("ANTES do filtro (props.groups)");

  const userId = loggedUserId.value;

  if (!userId) {
    console.warn(
      "⚠️ Usuário logado não encontrado (loggedUserId é nulo). Retornando apenas 'Todos'."
    );
    const onlyTodos = (props.groups || []).filter(
      (g) => (g.name || "").toLowerCase() === "todos"
    );
    return onlyTodos;
  }

  // Criadores cujos grupos o usuário pode escolher: ele mesmo e todos os
  // usuários ligados a ele por vínculo ativo de conta gerenciada — os titulares
  // de quem ele é membro e os membros que ele gerencia.
  const allowedCreatorIds = new Set([
    Number(userId),
    ...relatedUserIds.value.map((id) => Number(id)),
  ]);

  const filtered = (props.groups || []).filter((group) => {
    const groupName = (group.name || "").toLowerCase();
    const isTodos = groupName === "todos";
    const groupCreatorId = getGroupCreatorId(group);
    const isCreatedByAllowedUser =
      groupCreatorId != null && allowedCreatorIds.has(Number(groupCreatorId));
    return isTodos || isCreatedByAllowedUser;
  });

  const todosGroup = filtered.find((g) => (g.name || "").toLowerCase() === "todos");
  const otherGroups = filtered.filter((g) => (g.name || "").toLowerCase() !== "todos");
  const result = todosGroup ? [todosGroup, ...otherGroups] : otherGroups;

  console.log(
    `✅ Grupos FILTRADOS (userId: ${userId}, vinculados: [${[
      ...allowedCreatorIds,
    ].join(", ")}], Total: ${result.length})`,
    result.map((g) => ({
      id: g.id,
      name: g.name,
      createdByUserId: getGroupCreatorId(g),
    }))
  );

  return result;
});

const placeholderText = computed(() =>
  selectedOption.value === "users" ? "Selecione um usuário" : "Selecione um grupo"
);

const selectedEntity = computed({
  get: () => (selectedOption.value === "users" ? selectedUser.value : selectedGroup.value),
  set: (val) => {
    if (selectedOption.value === "users") selectedUser.value = val;
    else selectedGroup.value = val;
    // Limpa erro ao selecionar
    clearInlineError();
  },
});

const multiselectOptions = computed(() => {
  if (selectedOption.value === "users") {
    return [...(props.users || [])].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "pt-BR", { sensitivity: "base" })
    );
  }
  return filteredGroups.value;
});

function addPermission() {
  clearInlineError();

  if (!selectedEntity.value && selectedPermissions.value.length === 0) {
    inlineErrorEntity.value = "Selecione um usuário ou grupo.";
    inlineErrorPermissions.value = "Selecione ao menos uma permissão.";
    return;
  }

  if (!selectedEntity.value) {
    inlineErrorEntity.value = "Selecione um usuário ou grupo.";
    return;
  }

  if (selectedPermissions.value.length === 0) {
    inlineErrorPermissions.value = "Selecione ao menos uma permissão.";
    return;
  }

  const type = selectedOption.value === "users" ? "usuário" : "grupo";
  const alreadyExists = mappedPermissions.value.some((p, idx) => {
    if (isEditing.value && editIndex.value === idx) return false;
    return p.type === type && p.entityId === selectedEntity.value.id;
  });

  if (alreadyExists) {
    inlineErrorEntity.value = "Este usuário ou grupo já foi adicionado.";
    return;
  }

  const newPermission = {
    entityId: selectedEntity.value.id,
    name: selectedEntity.value.name,
    type,
    permissions: selectedPermissions.value.map((p) => p.name),
    permissionIds: selectedPermissions.value.map((p) => p.id),
    photoPath:
      type === "usuário"
        ? resolveUserPhotoPath(selectedEntity.value) ||
          getUserPhotoPathById(selectedEntity.value.id)
        : null,
  };

  const currentPermissions = [...mappedPermissions.value];
  if (isEditing.value && editIndex.value !== null) {
    currentPermissions[editIndex.value] = newPermission;
  } else {
    currentPermissions.push(newPermission);
  }

  mappedPermissions.value = currentPermissions;

  emitUpdate();
  resetForm();
}

function editEntry(index) {
  const entry = mappedPermissions.value[index];
  if (isLockedEntry(entry)) return;

  isEditing.value = true;
  editIndex.value = index;
  selectedOption.value = entry.type === "usuário" ? "users" : "groups";

  nextTick(() => {
    // Recupera o objeto completo da prop (para manter photoPath no selectedEntity)
    const sourceList = entry.type === "usuário" ? props.users : props.groups;
    const fullEntity = sourceList.find((e) => e.id === entry.entityId);
    selectedEntity.value = fullEntity || { id: entry.entityId, name: entry.name };

    selectedPermissions.value = entry.permissionIds
      .map((id) => props.permissions.find((p) => p.id === id))
      .filter(Boolean);

    nameInput.value?.$el?.querySelector("input")?.focus();
  });
}

function removeEntry(index) {
  const entry = mappedPermissions.value[index];
  if (isLockedEntry(entry)) return;

  mappedPermissions.value.splice(index, 1);
  emitUpdate();
  if (isEditing.value && editIndex.value === index) resetForm();
}

function emitUpdate() {
  const newPayload = mappedPermissions.value.flatMap((entry) =>
    entry.permissionIds.map((pid) => ({
      entityId: entry.entityId,
      type: entry.type === "usuário" ? "user" : "group",
      permissionId: pid,
    }))
  );
  emit("update:payload", newPayload);
}

async function resetForm() {
  selectedUser.value = null;
  selectedGroup.value = null;
  selectedPermissions.value = [];
  isEditing.value = false;
  editIndex.value = null;
  clearInlineError();
}

function validateBeforeConfirm() {
  clearInlineError();

  const hasSelectedEntity = !!selectedEntity.value;
  const hasSelectedPerms = selectedPermissions.value.length > 0;

  const isUserMode = selectedOption.value === "users";
  const subjectLabel = isUserMode ? "usuário" : "grupo";
  const subjectName = selectedEntity.value?.name || "";
  const subjectDisplay = subjectName ? `${subjectLabel} "${subjectName}"` : `um ${subjectLabel}`;

  const permCount = selectedPermissions.value.length;
  const permLabel = permCount === 1 ? "permissão" : "permissões";

  // 1) Apenas entidade selecionada, sem permissões
  if (hasSelectedEntity && !hasSelectedPerms) {
    inlineErrorPermissions.value = `Selecione ao menos uma permissão para ${subjectDisplay}.`;
    return false;
  }

  // 2) Apenas permissões selecionadas, sem entidade
  if (!hasSelectedEntity && hasSelectedPerms) {
    inlineErrorEntity.value = `Selecione um ${subjectLabel} para atribuir as ${permCount} ${permLabel} escolhidas.`;
    return false;
  }

  // 3) Entidade + permissões preenchidas mas não adicionadas à tabela
  if (hasSelectedEntity && hasSelectedPerms) {
    inlineErrorAction.value =
      `Clique em "Adicionar permissão" antes de confirmar.`;
    return false;
  }

  return true;
}

defineExpose({
  validateBeforeConfirm,
});

onBeforeUnmount(() => {
  // nada a limpar
});
</script>

<template>
  <div class="permission-form-wrapper">
    <div class="mb-2">
      <label class="text-base font-medium text-gray-900 ">
        Selecione quem terá acesso
        <template v-if="props.entityType === 'Text'">ao texto</template>
        <template v-else-if="props.entityType === 'Activity'">à atividade</template>
        <template v-else-if="props.entityType === 'Note'">à nota</template>
      </label>
    </div>

    <div class="flex mb-4">
      <div class="flex items-center me-4">
        <input
          id="radio-users"
          type="radio"
          value="users"
          v-model="selectedOption"
          @change="clearInlineError"
          class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
        />
        <label
          for="radio-users"
          class="ms-2 text-sm font-medium text-gray-900 "
          >Usuário</label
        >
      </div>
      <div class="flex items-center">
        <input
          id="radio-groups"
          type="radio"
          value="groups"
          v-model="selectedOption"
          @change="clearInlineError"
          class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
        />
        <label
          for="radio-groups"
          class="ms-2 text-sm font-medium text-gray-900 "
          >Grupo</label
        >
      </div>
    </div>

    <div v-if="selectedOption === 'users' || selectedOption === 'groups'">
      <div class="mb-4 select-row">
        <Multiselect
          ref="nameInput"
          v-model="selectedEntity"
          :options="multiselectOptions"
          :placeholder="placeholderText"
          label="name"
          track-by="id"
          :allow-empty="true"
          :show-labels="false"
          :class="{ 'multiselect--error': !!inlineErrorEntity }"
        >
          <!-- Item único selecionado -->
          <template #singleLabel="{ option }">
            <div class="flex items-center gap-2">
              <template v-if="selectedOption === 'users'">
                <div class="entity-avatar">
                  <div
                    v-if="getAvatarUrl(resolveUserPhotoPath(option))"
                    class="entity-avatar-img"
                    :style="{
                      backgroundImage: `url('${getAvatarUrl(
                        resolveUserPhotoPath(option)
                      )}')`,
                    }"
                  ></div>
                  <i v-else class="fas fa-user entity-avatar-icon"></i>
                </div>
              </template>

              <template v-else-if="selectedOption === 'groups'">
                <span class="entity-icon-group" title="Grupo">
                  <i class="fas fa-users"></i>
                </span>
              </template>

              <span>{{ option.name }}</span>
            </div>
          </template>

          <!-- Opções do dropdown -->
          <template #option="{ option }">
            <div class="flex items-center gap-2">
              <template v-if="selectedOption === 'users'">
                <div class="entity-avatar">
                  <div
                    v-if="getAvatarUrl(resolveUserPhotoPath(option))"
                    class="entity-avatar-img"
                    :style="{
                      backgroundImage: `url('${getAvatarUrl(
                        resolveUserPhotoPath(option)
                      )}')`,
                    }"
                  ></div>
                  <i v-else class="fas fa-user entity-avatar-icon"></i>
                </div>
              </template>

              <template v-else-if="selectedOption === 'groups'">
                <span v-if="option.name === 'Todos'">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    class="size-5"
                  >
                    <path
                      d="M16.555 5.412a8.028 8.028 0 0 0-3.503-2.81 14.899 14.899 0 0 1 1.663 4.472 8.547 8.547 0 0 0 1.84-1.662ZM13.326 7.825a13.43 13.43 0 0 0-2.413-5.773 8.087 8.087 0 0 0-1.826 0 13.43 13.43 0 0 0-2.413 5.773A8.473 8.473 0 0 0 10 8.5c1.18 0 2.304-.24 3.326-.675ZM6.514 9.376A9.98 9.98 0 0 0 10 10c1.226 0 2.4-.22 3.486-.624a13.54 13.54 0 0 1-.351 3.759A13.54 13.54 0 0 1 10 13.5c-1.079 0-2.128-.127-3.134-.366a13.538 13.538 0 0 1-.352-3.758ZM5.285 7.074a14.9 14.9 0 0 1 1.663-4.471 8.028 8.028 0 0 0-3.503 2.81c.529.638 1.149 1.199 1.84 1.66ZM17.334 6.798a7.973 7.973 0 0 1 .614 4.115 13.47 13.47 0 0 1-3.178 1.72 15.093 15.093 0 0 0 .174-3.939 10.043 10.043 0 0 0 2.39-1.896ZM2.666 6.798a10.042 10.042 0 0 0 2.39 1.896 15.196 15.196 0 0 0 .174 3.94 13.472 13.472 0 0 1-3.178-1.72 7.973 7.973 0 0 1 .615-4.115ZM10 15c.898 0 1.778-.079 2.633-.23a13.473 13.473 0 0 1-1.72 3.178 8.099 8.099 0 0 1-1.826 0 13.47 13.47 0 0 1-1.72-3.178c.855.151 1.735.23 2.633.23ZM14.357 14.357a14.912 14.912 0 0 1-1.305 3.04 8.027 8.027 0 0 0 4.345-4.345c-.953.542-1.971.981-3.04 1.305ZM6.948 17.397a8.027 8.027 0 0 1-4.345-4.345c.953.542 1.971.981 3.04 1.305a14.912 14.912 0 0 0 1.305 3.04Z"
                    />
                  </svg>
                </span>

                <svg
                  v-else
                  class="w-5 h-5 shrink-0"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M4.5 6.375a4.125 4.125 0 1 1 8.25 0 4.125 4.125 0 0 1-8.25 0ZM14.25 8.625a3.375 3.375 0 1 1 6.75 0 3.375 3.375 0 0 1-6.75 0ZM1.5 19.125a7.125 7.125 0 0 1 14.25 0v.003l-.001.119a.75.75 0 0 1-.363.63 13.067 13.067 0 0 1-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 0 1-.364-.63l-.001-.122ZM17.25 19.128l-.001.144a2.25 2.25 0 0 1-.233.96 10.088 10.088 0 0 0 5.06-1.01.75.75 0 0 0 .42-.643 4.875 4.875 0 0 0-6.957-4.611 8.586 8.586 0 0 1 1.71 5.157v.003Z"
                  />
                </svg>
              </template>

              <span>{{ option.name }}</span>
            </div>
          </template>
        </Multiselect>

        <!-- Erro: entidade -->
        <p v-if="inlineErrorEntity" class="field-error">
          <i class="fas fa-exclamation-circle field-error__icon"></i>
          <span>{{ inlineErrorEntity }}</span>
        </p>
      </div>

      <div class="flex items-end gap-3 mb-4 form-actions">
        <div class="perm-col">
          <label class="block mb-2 text-sm font-medium text-gray-900 "
            >Indique as permissões</label
          >

          <Multiselect
            v-model="selectedPermissions"
            :options="props.permissions"
            placeholder="Selecione permissões"
            label="name"
            track-by="id"
            :multiple="true"
            :close-on-select="false"
            :show-labels="false"
            :class="{ 'multiselect--error': !!inlineErrorPermissions }"
            @update:modelValue="clearInlineError"
          >
            <template #tag="{ option, remove }">
              <span class="ms-chip">
                <span class="ms-chip-link">{{ option.name }}</span>

                <button
                  type="button"
                  class="ms-chip-remove"
                  @click.stop="remove(option)"
                  aria-label="Remover"
                >
                  ×
                </button>
              </span>
            </template>
          </Multiselect>

          <!-- Erro: permissões -->
          <p v-if="inlineErrorPermissions" class="field-error">
            <i class="fas fa-exclamation-circle field-error__icon"></i>
            <span>{{ inlineErrorPermissions }}</span>
          </p>
        </div>

        <div class="btn-add-col">
          <button type="button" @click="addPermission" class="btn btn-primary">
            {{ isEditing ? "Atualizar permissão" : "Adicionar permissão" }}
          </button>

          <!-- Erro: ação pendente -->
          <p v-if="inlineErrorAction" class="field-error field-error--action">
            <i class="fas fa-exclamation-circle field-error__icon"></i>
            <span>{{ inlineErrorAction }}</span>
          </p>
        </div>

        <button
          v-if="!isEditing"
          type="button"
          @click="resetForm"
          class="btn btn-secondary btn-reset"
        >
          Resetar
        </button>
      </div>
    </div>

    <div v-if="mappedPermissions.length > 0" class="mt-5">
      <h4 class="mb-1 text-sm font-bold text-gray-700 ">
        Permissões adicionadas
      </h4>

      <p v-if="mappedPermissions.length" class="precedence-note">
        A permissão dada a um usuário especificamente substitui a que ele receberia pelos grupos
        dele. Entre grupos, as permissões se somam.
      </p>

      <div class="table-wrap">
        <table class="w-full table-fixed text-xs text-left text-gray-500 ">
          <colgroup>
            <col class="col-name" />
            <col class="col-type" />
            <col class="col-permissions" />
            <col class="col-actions" />
          </colgroup>

          <thead
            class="text-[11px] text-gray-700 uppercase bg-gray-50 "
          >
            <tr>
              <th class="px-4 py-2">Nome</th>
              <th class="px-4 py-2">Tipo</th>
              <th class="px-4 py-2">Permissões</th>
              <th class="px-4 py-2 text-center">Ações</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(entry, index) in entriesToDisplay"
              :key="index"
              class="bg-surface border-b "
            >
              <td class="px-4 py-2 font-medium text-gray-900 ">
                <span class="inline-flex items-start gap-2 name-cell">
                  <!-- Usuário: avatar real ou fallback -->
                  <template v-if="entry.type === 'usuário'">
                    <div class="entity-avatar entity-avatar--sm">
                      <div
                        v-if="getAvatarUrl(entry.photoPath)"
                        class="entity-avatar-img"
                        :style="{
                          backgroundImage: `url('${getAvatarUrl(entry.photoPath)}')`,
                        }"
                      ></div>
                      <i v-else class="fas fa-user entity-avatar-icon entity-avatar-icon--sm"></i>
                    </div>
                  </template>

                  <!-- Grupo "Todos" -->
                  <svg
                    v-else-if="(entry.name || '').toLowerCase() === 'todos'"
                    class="w-4 h-4 shrink-0"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      d="M16.555 5.412a8.028 8.028 0 0 0-3.503-2.81 14.899 14.899 0 0 1 1.663 4.472 8.547 8.547 0 0 0 1.84-1.662ZM13.326 7.825a13.43 13.43 0 0 0-2.413-5.773 8.087 8.087 0 0 0-1.826 0 13.43 13.43 0 0 0-2.413 5.773A8.473 8.473 0 0 0 10 8.5c1.18 0 2.304-.24 3.326-.675ZM6.514 9.376A9.98 9.98 0 0 0 10 10c1.226 0 2.4-.22 3.486-.624a13.54 13.54 0 0 1-.351 3.759A13.54 13.54 0 0 1 10 13.5c-1.079 0-2.128-.127-3.134-.366a13.538 13.538 0 0 1-.352-3.758ZM5.285 7.074a14.9 14.9 0 0 1 1.663-4.471 8.028 8.028 0 0 0-3.503 2.81c.529.638 1.149 1.199 1.84 1.66ZM17.334 6.798a7.973 7.973 0 0 1 .614 4.115 13.47 13.47 0 0 1-3.178 1.72 15.093 15.093 0 0 0 .174-3.939 10.043 10.043 0 0 0 2.39-1.896ZM2.666 6.798a10.042 10.042 0 0 0 2.39 1.896 15.196 15.196 0 0 0 .174 3.94 13.472 13.472 0 0 1-3.178-1.72 7.973 7.973 0 0 1 .615-4.115ZM10 15c.898 0 1.778-.079 2.633-.23a13.473 13.473 0 0 1-1.72 3.178 8.099 8.099 0 0 1-1.826 0 13.47 13.47 0 0 1-1.72-3.178c.855.151 1.735.23 2.633.23ZM14.357 14.357a14.912 14.912 0 0 1-1.305 3.04 8.027 8.027 0 0 0 4.345-4.345c-.953.542-1.971.981-3.04 1.305ZM6.948 17.397a8.027 8.027 0 0 1-4.345-4.345c.953.542 1.971.981 3.04 1.305a14.912 14.912 0 0 0 1.305 3.04Z"
                    />
                  </svg>

                  <!-- Outros grupos -->
                  <svg
                    v-else
                    class="w-4 h-4 shrink-0"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path
                      d="M4.5 6.375a4.125 4.125 0 1 1 8.25 0 4.125 4.125 0 0 1-8.25 0ZM14.25 8.625a3.375 3.375 0 1 1 6.75 0 3.375 3.375 0 0 1-6.75 0ZM1.5 19.125a7.125 7.125 0 0 1 14.25 0v.003l-.001.119a.75.75 0 0 1-.363.63 13.067 13.067 0 0 1-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 0 1-.364-.63l-.001-.122ZM17.25 19.128l-.001.144a2.25 2.25 0 0 1-.233.96 10.088 10.088 0 0 0 5.06-1.01.75.75 0 0 0 .42-.643 4.875 4.875 0 0 0-6.957-4.611 8.586 8.586 0 0 1 1.71 5.157v.003Z"
                    />
                  </svg>

                  <span class="name-cell-info">
                    <span class="name-cell-title">{{ entry.name }}</span>
                    <span
                      v-if="isOwnerEntry(entry) || isManagerEntry(entry) || overriddenGroupNames(entry).length"
                      class="name-cell-badges"
                    >
                      <span v-if="isOwnerEntry(entry)" class="owner-badge">
                        proprietário
                      </span>
                      <span v-else-if="isManagerEntry(entry)" class="owner-badge">
                        (titular)
                      </span>
                      <span
                        v-if="overriddenGroupNames(entry).length"
                        class="override-badge"
                        :title="`Só vale esta linha para ${entry.name}: ela substitui a permissão que ele teria por ${overriddenGroupNames(entry).join(', ')}.`"
                      >
                        substitui {{ overriddenGroupNames(entry).join(", ") }}
                      </span>
                    </span>
                  </span>
                </span>
              </td>

              <td class="px-4 py-2 capitalize">{{ entry.type }}</td>

              <td class="px-4 py-2 text-[11px]">
                {{ entry.permissions.join(", ") }}
              </td>

              <td class="px-4 py-2">
                <div class="flex justify-center items-center space-x-2">
                  <button
                    type="button"
                    :disabled="isLockedEntry(entry)"
                    :class="[
 'btn-icon',
                      'btn-icon-edit',
                      { 'btn-icon-disabled': isLockedEntry(entry) },
                    ]"
                    @click="!isLockedEntry(entry) && editEntry(index)"
                    title="Editar"
                  >
                    <i class="fas fa-pencil-alt"></i>
                  </button>

                  <button
                    type="button"
                    :disabled="isLockedEntry(entry)"
                    :class="[
 'btn-icon',
                      'btn-icon-delete',
                      { 'btn-icon-disabled': isLockedEntry(entry) },
                    ]"
                    @click="!isLockedEntry(entry) && removeEntry(index)"
                    title="Apagar"
                  >
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.permission-form-wrapper {
  font-family: inherit;
  background-color: inherit;
  max-width: 820px;
  width: 100%;
}

.select-row,
.perm-col {
  width: 321px;
  max-width: 100%;
}

.form-actions {
  max-width: 820px;
  align-items: flex-start;
}

.btn-add-col {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;

  /* ✅ Centraliza verticalmente o botão com o dropdown "Indique as permissões" */
  padding-top: 1.95rem;
}

.btn-reset {
  /* ✅ Centraliza verticalmente o botão Resetar com o dropdown "Indique as permissões" */
  margin-top: 1.95rem;
}

.table-wrap {
  max-width: 820px;
}

.table-wrap table {
  table-layout: fixed;
}

.table-wrap col.col-name {
  width: 45%;
}

.table-wrap col.col-type {
  width: 18%;
}

.table-wrap col.col-permissions {
  width: 22%;
}

.table-wrap col.col-actions {
  width: 15%;
}

.table-wrap th,
.table-wrap td {
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
}

.name-cell {
  width: 100%;
}

.name-cell-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.name-cell-title {
  overflow-wrap: break-word;
  word-break: break-word;
}

.name-cell-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.15rem;
}

/* =========================
   AVATARES
========================= */
.entity-avatar {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  background-color: var(--border-color);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex: 0 0 26px;
}

.entity-avatar--sm {
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
}

.entity-avatar-img {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
}

.entity-avatar-icon {
  font-size: 13px;
  color: var(--text-muted);
}

.entity-avatar-icon--sm {
  font-size: 10px;
}

.entity-icon-group {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  background-color: var(--border-color);
  color: var(--text-muted);
  border: 1px solid var(--border-color);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  flex: 0 0 26px;
}

.entity-icon-group i {
  font-size: 13px;
}

/* =========================
   ERRO INLINE
========================= */
.field-error {
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  margin-top: 0.4rem;
  font-size: 0.78rem;
  color: var(--danger-text);
  line-height: 1.4;
  white-space: pre-line;
}

.field-error--action {
  max-width: 260px;
}

.field-error__icon {
  font-size: 0.78rem;
  margin-top: 0.1rem;
  flex-shrink: 0;
}

/* Outline vermelho no multiselect quando há erro */
:deep(.multiselect--error) {
  border-color: #f87171 !important;
  box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.2) !important;
}

.btn-icon {
  width: 24px;
}

.btn-icon-edit {
  background-color: var(--brand-soft);
  color: #4338ca;
}

.btn-icon-delete {
  background-color: var(--danger-bg);
  color: var(--danger-text);
}

.btn-icon-disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none !important;
}

/* =========================
   MULTISELECT
========================= */
:deep(.multiselect) {
  border: 1px solid var(--border-color);
  border-radius: 10px;
  min-height: 38px;
}

:deep(.multiselect:focus-within) {
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}

:deep(.multiselect__tags) {
  border: none;
  background: transparent;
  min-height: 38px;

  /* ✅ Centraliza verticalmente chips, placeholder e conteúdo selecionado */
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  padding: 0.25rem 2.2rem 0.25rem 0.5rem;
}

/* Input e item selecionado */
:deep(.multiselect__input),
:deep(.multiselect__single) {
  background: transparent;
  font-size: 0.82rem;
  font-family: inherit;
}

/* ✅ Centraliza o conteúdo selecionado do dropdown usuário/grupo */
:deep(.multiselect__single) {
  display: flex;
  align-items: center;
  min-height: 28px;
  margin-bottom: 0;
  padding-top: 0;
}

/* ✅ Centraliza placeholder e input, incluindo "Selecione permissões" */
:deep(.multiselect__placeholder),
:deep(.multiselect__input) {
  display: flex;
  align-items: center;
  min-height: 28px;
  margin-bottom: 0;
  padding-top: 0;
}

/* ✅ Centraliza a seta dos dropdowns */
:deep(.multiselect__select) {
  height: 38px;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ✅ Corrige a seta gerada por pseudo-elemento */
:deep(.multiselect__select::before) {
  position: static;
  margin-top: 0;
}

/* Opções do dropdown */
:deep(.multiselect__option) {
  font-size: 0.82rem;
  padding: 8px 10px;
}

:deep(.multiselect__option--highlight) {
  background-color: var(--focus-border);
  color: var(--text-on-brand);
}

/* ✅ Centraliza avatar/ícone/texto no item selecionado e nas opções */
:deep(.multiselect__single > div),
:deep(.multiselect__option > div) {
  display: flex;
  align-items: center;
}

/* ✅ Centraliza os chips do dropdown "Indique as permissões" */
:deep(.multiselect__tags-wrap) {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

/* Badge de proprietário */
.owner-badge {
  margin-left: 0.25rem;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.08rem 0.32rem;
  border-radius: 9999px;
  background-color: #e0f2fe;
  color: #0369a1;
}

.override-badge {
  margin-left: 0.25rem;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.08rem 0.32rem;
  border-radius: 9999px;
  background-color: var(--warning-bg);
  color: var(--warning-text);
}

.precedence-note {
  margin-top: 0;
  margin-bottom: 0.75rem;
  font-size: 0.75rem;
  color: var(--text-muted);
  background: transparent;
  text-align: justify;
}

/* =========================
   CHIPS
========================= */
.ms-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border-radius: 7px;
  border: 1px solid var(--neutral-400);
  background: var(--surface-3);
  font-size: 0.85rem;
  margin: 2px 6px 2px 0;
}

.ms-chip-remove {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0 2px;
  color: var(--text-muted);
}

.ms-chip-remove:hover {
  color: #0f172a;
}

.ms-chip-link {
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;

  cursor: default;
  color: inherit;
  font: inherit;
  line-height: 1.1;

  text-decoration: none;
}

.ms-chip-link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.25);
  border-radius: 6px;
}
</style>